/**
 * Generic WMS raster overlay layer (#212) — the config-driven generalization of
 * the FWI heatmap (the platform's first imagery overlay). Mounts an official
 * government WMS raster *directly at view-time* via a Cesium
 * `WebMapServiceImageryProvider` — no ingest, no DB table, no migration.
 *
 * Both shipped overlays serve `image/png` with open CORS (live-verified by
 * GetMap), so the browser fetches tiles cross-origin with no proxy:
 *   - ECCC GeoMet hurricane forecast track (`geo.weather.gc.ca/geomet`,
 *     `HURRICANE_LINE`) — `Access-Control-Allow-Origin: *`.
 *   - NRCan EGS Active-Floods extent (`maps-cartes.services.geo.ca/egs_sgu`,
 *     layer `0`) — CORS reflects the Origin.
 *
 * A WMS raster is the AMBIENT register: opacity is capped at
 * `WMS_RASTER_MAX_ALPHA` (0.55) so markers / fires / anomalies below stay
 * readable (design-council Part IX / colour-law). Every overlay is default OFF
 * (DEFAULT_VIEW_LAYERS untouched) and honest-degrades — a WMS 4xx/5xx surfaces on
 * the provider's `errorEvent`, is logged, and never breaks the rest of the map.
 *
 * Like FwiHeatmapLayer / ProvincialBoundariesLayer, MapView owns the Cesium
 * viewer, so these are imperative installers returning handles. The layers are
 * added to `viewer.imageryLayers` (not `entities`) and shown/hidden via the
 * doctrinal group toggle.
 */
import * as Cesium from 'cesium';

/** Ambient-register opacity ceiling for a WMS raster overlay (colour-law §IX). */
export const WMS_RASTER_MAX_ALPHA = 0.55;

export interface WmsOverlayConfig {
  /** Stable key (also the visibility-map key + WMS_OVERLAYS registry key). */
  key: string;
  /** Operator-facing label (the panel toggle label). */
  label: string;
  /** WMS GetMap endpoint. */
  url: string;
  /** WMS layer name (single — GeoMet rejects multi-value LAYERS). */
  layers: string;
  /** Overlay opacity. Clamped to WMS_RASTER_MAX_ALPHA on install. */
  alpha: number;
  /** Attribution credit (OGL / ECCC / NRCan). */
  attribution: string;
  /** Tiling extent — scope requests to the data footprint (no global fan-out). */
  rectangle: Cesium.Rectangle;
  /** WMS version (default 1.3.0). */
  version?: string;
  /** Requested CRS (default EPSG:4326 for Cesium's GeographicTilingScheme). */
  crs?: string;
  /** WMS STYLES= value (default '' = the server's default style). The wall
   *  radar (#272, PART XI Ruling 1.4b) MUST pin a no-red-impersonation ramp,
   *  so the registry can name a specific server style. */
  styles?: string;
  /** Self-refresh cadence (ms) for an always-latest layer whose IMAGE changes
   *  under a stable URL (GeoMet radar updates every ~6 min). When set, the
   *  installer re-creates the provider on this cadence with a cache-busting
   *  parameter so stale tiles are re-fetched. Omitted = static (the default —
   *  hurricane track / flood extent refresh on toggle). */
  refreshMs?: number;
}

export interface WmsOverlayLayerHandle {
  /** The config this handle renders (for visibility lookups + tests). */
  readonly config: WmsOverlayConfig;
  /** Show/hide the WMS imagery overlay (doctrinal group toggle). */
  setVisible(visible: boolean): void;
  /** Currently-visible state (mirrors the underlying ImageryLayer.show). */
  isVisible(): boolean;
  /** Re-create the provider with a fresh cache-bust so the latest image is
   *  re-fetched (the radar ~6-min cadence). Preserves visibility + alpha. */
  refresh(): void;
  destroy(): void;
}

/**
 * Build a `WebMapServiceImageryProvider` for one overlay config. Pure (no viewer)
 * so it is unit-testable; installers wrap it.
 *
 * WMS 1.3.0 + `crs=EPSG:4326` means lat,lon axis order — Cesium handles the swap
 * internally given the default GeographicTilingScheme, so we never hand-build a
 * bbox. Empty `styles` selects the server default style (the official ramp the
 * operator recognises from the government's own tools); a config may pin a
 * specific style (the wall radar's no-red-impersonation ramp, Ruling 1.4b). No
 * TIME dimension — every mounted source is always-latest (GeoMet serves the
 * most recent radar pass when TIME is omitted); a self-refreshing layer
 * re-creates its provider with a cache-busting `_r` param instead, so Cesium's
 * tile cache (keyed by URL) can't pin a stale pass.
 */
export function buildWmsOverlayProvider(
  config: WmsOverlayConfig,
  opts: { cacheBust?: number } = {},
): Cesium.WebMapServiceImageryProvider {
  return new Cesium.WebMapServiceImageryProvider({
    url: config.url,
    layers: config.layers,
    parameters: {
      styles: config.styles ?? '',
      format: 'image/png',
      transparent: true,
      version: config.version ?? '1.3.0',
      // Cache-bust for self-refreshing layers: a changed value gives every
      // tile a fresh URL, forcing a re-fetch of the latest radar pass. WMS
      // ignores unknown vendor parameters, so this is wire-safe.
      ...(opts.cacheBust != null ? { _r: String(opts.cacheBust) } : {}),
    },
    // For WMS >= 1.3.0 the axis-ordered CRS is passed via `crs` (not `srs`).
    crs: config.crs ?? 'EPSG:4326',
    rectangle: config.rectangle,
    // No GetFeatureInfo — these are ambient background rasters, not clickable
    // (clicks fall through to the markers/fires/anomalies above them).
    enablePickFeatures: false,
    credit: config.attribution,
  });
}

/**
 * Install one WMS overlay as a Cesium imagery layer (above the base imagery,
 * below the screen-space entity markers). Starts hidden; MapView calls
 * `setVisible` from the group toggle. Opacity is clamped to the ambient ceiling.
 *
 * Defensive: a WMS 4xx/5xx surfaces on the provider's `errorEvent`; we log it and
 * let Cesium retry/skip the tile — a failed fetch must never break the map.
 */
export function installWmsOverlayLayer(
  viewer: Cesium.Viewer,
  config: WmsOverlayConfig,
  opts: { provider?: Cesium.WebMapServiceImageryProvider } = {},
): WmsOverlayLayerHandle {
  const install = (
    provider: Cesium.WebMapServiceImageryProvider,
  ): { layer: Cesium.ImageryLayer; removeErr: () => void } => {
    const removeErr = provider.errorEvent.addEventListener((err: unknown) => {
      console.warn(`[WmsOverlayLayer] ${config.key} WMS tile error (non-fatal)`, err);
    });
    const layer = viewer.imageryLayers.addImageryProvider(provider);
    // Ambient register: never exceed the opacity ceiling, whatever the config asks.
    layer.alpha = Math.min(config.alpha, WMS_RASTER_MAX_ALPHA);
    layer.show = false; // group toggle turns it on
    return { layer, removeErr };
  };

  let current = install(opts.provider ?? buildWmsOverlayProvider(config));
  // Monotonic cache-bust counter — each refresh() re-keys every tile URL.
  let refreshGen = 0;

  return {
    config,
    setVisible(visible: boolean): void {
      if (viewer.isDestroyed()) return;
      current.layer.show = visible;
    },
    isVisible(): boolean {
      return !viewer.isDestroyed() && current.layer.show;
    },
    refresh(): void {
      if (viewer.isDestroyed()) return;
      const wasVisible = current.layer.show;
      current.removeErr();
      viewer.imageryLayers.remove(current.layer, true);
      refreshGen += 1;
      current = install(buildWmsOverlayProvider(config, { cacheBust: refreshGen }));
      current.layer.show = wasVisible;
    },
    destroy(): void {
      current.removeErr();
      if (viewer.isDestroyed()) return;
      viewer.imageryLayers.remove(current.layer, true);
    },
  };
}

/** Manager handle for the full set of WMS overlays (mirrors ArcgisCategoryLayers). */
export interface WmsOverlaysHandle {
  /** Apply each overlay's visibility from the supplied getter. */
  applyVisibility(): void;
  destroy(): void;
}

/**
 * Install every overlay in `configs` and fan visibility/teardown across them.
 * `getVisibility(key)` is read live (so MapView can route through `propsRef`).
 *
 * A config with `refreshMs` set (the wall radar) gets a self-refresh timer:
 * while VISIBLE, its provider is re-created on that cadence so the latest
 * radar pass is re-fetched (Ruling 1.4d); `onRefresh` reports each pass for
 * the honest timestamp line. Hidden overlays skip the re-fetch (no work for
 * an invisible raster) and refresh on the next toggle-on instead.
 */
export function installWmsOverlays(
  viewer: Cesium.Viewer,
  configs: readonly WmsOverlayConfig[],
  opts: {
    getVisibility: (key: string) => boolean;
    onRefresh?: (key: string, atMs: number) => void;
  },
): WmsOverlaysHandle {
  const handles = configs.map((c) => installWmsOverlayLayer(viewer, c));
  const timers: ReturnType<typeof setInterval>[] = [];
  // Keys whose refresh was SKIPPED while hidden — they re-fetch on toggle-on.
  const staleWhileHidden = new Set<string>();

  for (const h of handles) {
    const ms = h.config.refreshMs;
    if (ms != null && ms > 0) {
      timers.push(
        setInterval(() => {
          if (h.isVisible()) {
            h.refresh();
            opts.onRefresh?.(h.config.key, Date.now());
          } else {
            staleWhileHidden.add(h.config.key);
          }
        }, ms),
      );
    }
  }

  return {
    applyVisibility(): void {
      for (const h of handles) {
        const want = opts.getVisibility(h.config.key);
        // A self-refreshing layer that went stale while hidden re-fetches as
        // it comes back on, so a toggle-on never shows an old pass.
        if (want && !h.isVisible() && staleWhileHidden.delete(h.config.key)) {
          h.refresh();
          opts.onRefresh?.(h.config.key, Date.now());
        }
        h.setVisible(want);
      }
    },
    destroy(): void {
      for (const t of timers) clearInterval(t);
      for (const h of handles) h.destroy();
    },
  };
}
